import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { Code, Type } from 'lucide-react';
import { prepareBlogHtml } from '../../utils/prepareBlogHtml';

const toolbarButtonClass =
  'px-2 py-1 text-xs font-medium rounded border border-stone-300 bg-white hover:bg-stone-50 text-stone-700';

/**
 * Blog body editor: visual (contenteditable) or raw HTML.
 * Visual mode is intended for wording tweaks; complex layout/diagrams stay in HTML mode.
 */
const JournalContentEditor = forwardRef(({
  value,
  onChange,
  rows = 16,
  accent = 'blue',
  label = 'Article body',
}, ref) => {
  const [mode, setMode] = useState('html');
  const visualRef = useRef(null);
  const labelClass = accent === 'yellow' ? 'text-yellow-800' : 'text-blue-800';

  useEffect(() => {
    if (mode !== 'visual' || !visualRef.current) return;
    if (visualRef.current.dataset.syncedValue === value) return;
    visualRef.current.innerHTML = prepareBlogHtml(value || '');
    visualRef.current.dataset.syncedValue = value || '';
  }, [mode, value]);

  const syncVisualToHtml = () => {
    if (!visualRef.current) return value;
    const raw = visualRef.current.innerHTML;
    const clean = DOMPurify.sanitize(raw, {
      ADD_TAGS: ['style', 'section', 'article', 'header', 'footer', 'svg', 'path', 'pre', 'code'],
      ADD_ATTR: [
        'class',
        'id',
        'href',
        'src',
        'alt',
        'target',
        'rel',
        'style',
        'colspan',
        'rowspan',
        'type',
      ],
      ALLOW_DATA_ATTR: true,
    });
    onChange(clean);
    if (visualRef.current) visualRef.current.dataset.syncedValue = clean;
    return clean;
  };

  useImperativeHandle(ref, () => ({
    flush: () => {
      if (mode === 'visual') return syncVisualToHtml();
      return value;
    },
  }), [mode, value]);

  const switchMode = (next) => {
    if (next === mode) return;
    if (next === 'html' && mode === 'visual') {
      syncVisualToHtml();
    }
    if (next === 'visual') {
      setMode('visual');
      return;
    }
    setMode('html');
  };

  const exec = (command, arg = null) => {
    visualRef.current?.focus();
    document.execCommand(command, false, arg);
    syncVisualToHtml();
  };

  const handleLink = () => {
    const url = window.prompt('Link URL');
    if (url) exec('createLink', url);
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className={`block text-sm font-medium ${labelClass}`}>{label}</label>
        <div className="flex rounded-lg border border-stone-200 overflow-hidden text-xs">
          <button
            type="button"
            onClick={() => switchMode('visual')}
            className={`flex items-center gap-1 px-3 py-1.5 ${
              mode === 'visual' ? 'bg-stone-900 text-white' : 'bg-white text-stone-600'
            }`}
          >
            <Type className="w-3.5 h-3.5" />
            Visual
          </button>
          <button
            type="button"
            onClick={() => switchMode('html')}
            className={`flex items-center gap-1 px-3 py-1.5 ${
              mode === 'html' ? 'bg-stone-900 text-white' : 'bg-white text-stone-600'
            }`}
          >
            <Code className="w-3.5 h-3.5" />
            HTML
          </button>
        </div>
      </div>

      {mode === 'visual' ? (
        <>
          <div className="flex flex-wrap gap-1 p-2 bg-stone-50 border border-stone-200 rounded-t-lg">
            <button type="button" className={toolbarButtonClass} onClick={() => exec('bold')}>
              Bold
            </button>
            <button type="button" className={toolbarButtonClass} onClick={() => exec('italic')}>
              Italic
            </button>
            <button type="button" className={toolbarButtonClass} onClick={() => exec('underline')}>
              Underline
            </button>
            <button type="button" className={toolbarButtonClass} onClick={() => exec('formatBlock', 'h2')}>
              Heading
            </button>
            <button type="button" className={toolbarButtonClass} onClick={() => exec('insertUnorderedList')}>
              Bullets
            </button>
            <button type="button" className={toolbarButtonClass} onClick={() => exec('insertOrderedList')}>
              Numbered
            </button>
            <button type="button" className={toolbarButtonClass} onClick={handleLink}>
              Link
            </button>
          </div>
          <div
            ref={visualRef}
            contentEditable
            suppressContentEditableWarning
            onInput={() => syncVisualToHtml()}
            className="min-h-[280px] max-h-[70vh] overflow-y-auto p-4 border border-t-0 border-stone-200 rounded-b-lg bg-white prose prose-sm max-w-none blog-content focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <p className="text-xs text-stone-500">
            Edit text directly like a document. For Mermaid diagrams, tables, or layout blocks, switch to{' '}
            <strong>HTML</strong>.
          </p>
        </>
      ) : (
        <>
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={rows}
            className="field font-mono text-sm"
            required
            placeholder="HTML content…"
          />
          <p className="text-xs text-stone-500">
            Full HTML source. Use <strong>Visual</strong> for quick wording changes in paragraphs and lists.
          </p>
        </>
      )}
    </div>
  );
});

JournalContentEditor.displayName = 'JournalContentEditor';

export default JournalContentEditor;
